/* eslint-disable hum/restrict-constants-imports */
/* eslint-disable hum/require-constants-import */
import {
    AccountLabels,
    AccountLabel,
} from "@/entities/DataAssociation/Account.model";
import { StatementType } from "@/entities/DataAssociation/File.model";
import {
    AccountCategory,
    IncomeStatementCategory,
    BalanceSheetCategory,
    OtherCategory,
} from "@/entities/DataAssociation/Account.model";

export type LabelMetadata = {
    statementType: StatementType;
    name: string;
    category: AccountCategory;
    code: AccountLabel;
};

export const ACCOUNT_LIST: Record<AccountLabel, LabelMetadata> = {
    [AccountLabels.REVENUES_COHORT]: {
        statementType: StatementType.INCOME_STATEMENT,
        name: "Cohort-able Revenue",
        category: IncomeStatementCategory.REVENUES,
        code: AccountLabels.REVENUES_COHORT,
    },
    [AccountLabels.REVENUES_NON_COHORT]: {
        statementType: StatementType.INCOME_STATEMENT,
        name: "Non Cohort-able Revenue with Shared COGS",
        category: IncomeStatementCategory.REVENUES,
        code: AccountLabels.REVENUES_NON_COHORT,
    },
    [AccountLabels.REVENUES_NON_COHORT_SEPARATE_COGS]: {
        statementType: StatementType.INCOME_STATEMENT,
        name: "Non Cohort-able Revenue with Separate COGS",
        category: IncomeStatementCategory.REVENUES,
        code: AccountLabels.REVENUES_NON_COHORT_SEPARATE_COGS,
    },
    [AccountLabels.REVENUES_NON_COHORT_NO_COGS]: {
        statementType: StatementType.INCOME_STATEMENT,
        name: "Non Cohort-able Revenue without COGS",
        category: IncomeStatementCategory.REVENUES,
        code: AccountLabels.REVENUES_NON_COHORT_NO_COGS,
    },
    [AccountLabels.REVENUES_NON_RECURRING]: {
        statementType: StatementType.INCOME_STATEMENT,
        name: "Non Recurring Revenue",
        category: IncomeStatementCategory.REVENUES,
        code: AccountLabels.REVENUES_NON_RECURRING,
    },

    [AccountLabels.EXPENSES_D_AND_A]: {
        statementType: StatementType.INCOME_STATEMENT,
        name: "Depreciation & Amortization",
        category: IncomeStatementCategory.EXPENSES,
        code: AccountLabels.EXPENSES_D_AND_A,
    },
    [AccountLabels.EXPENSES_GROSS_CASHFLOW_RELATED]: {
        statementType: StatementType.INCOME_STATEMENT,
        name: "GMV Deductions",
        category: IncomeStatementCategory.EXPENSES,
        code: AccountLabels.EXPENSES_GROSS_CASHFLOW_RELATED,
    },
    [AccountLabels.EXPENSES_NON_CASH_EXPENSES]: {
        statementType: StatementType.INCOME_STATEMENT,
        name: "Non-Cash Expenses",
        category: IncomeStatementCategory.EXPENSES,
        code: AccountLabels.EXPENSES_NON_CASH_EXPENSES,
    },
    [AccountLabels.EXPENSES_NON_RECURRING]: {
        statementType: StatementType.INCOME_STATEMENT,
        name: "Non-Recurring Expenses",
        category: IncomeStatementCategory.EXPENSES,
        code: AccountLabels.EXPENSES_NON_RECURRING,
    },
    [AccountLabels.EXPENSES_OPEX_RELATED]: {
        statementType: StatementType.INCOME_STATEMENT,
        name: "Operating Expenses",
        category: IncomeStatementCategory.EXPENSES,
        code: AccountLabels.EXPENSES_OPEX_RELATED,
    },
    [AccountLabels.EXPENSES_R_AND_D_RELATED]: {
        statementType: StatementType.INCOME_STATEMENT,
        name: "Research & Development Expenses",
        category: IncomeStatementCategory.EXPENSES,
        code: AccountLabels.EXPENSES_R_AND_D_RELATED,
    },
    [AccountLabels.EXPENSES_REVENUE_RELATED_FINANCIAL_PRINCIPAL_RECOUPMENT]: {
        statementType: StatementType.INCOME_STATEMENT,
        name: "Financial Principal Recoupment",
        category: IncomeStatementCategory.EXPENSES,
        code: AccountLabels.EXPENSES_REVENUE_RELATED_FINANCIAL_PRINCIPAL_RECOUPMENT,
    },
    [AccountLabels.EXPENSES_REVENUE_RELATED_INVENTORY_RECOUPMENT]: {
        statementType: StatementType.INCOME_STATEMENT,
        name: "Inventory COGS",
        category: IncomeStatementCategory.EXPENSES,
        code: AccountLabels.EXPENSES_REVENUE_RELATED_INVENTORY_RECOUPMENT,
    },
    [AccountLabels.EXPENSES_REVENUE_RELATED_INVENTORY_RECOUPMENT_NON_COHORT]: {
        statementType: StatementType.INCOME_STATEMENT,
        name: "Non-Cohort-able Inventory COGS",
        category: IncomeStatementCategory.EXPENSES,
        code: AccountLabels.EXPENSES_REVENUE_RELATED_INVENTORY_RECOUPMENT_NON_COHORT,
    },
    [AccountLabels.EXPENSES_REVENUE_RELATED_NON_INVENTORY_COGS]: {
        statementType: StatementType.INCOME_STATEMENT,
        name: "Non-Inventory COGS",
        category: IncomeStatementCategory.EXPENSES,
        code: AccountLabels.EXPENSES_REVENUE_RELATED_NON_INVENTORY_COGS,
    },
    [AccountLabels.EXPENSES_REVENUE_RELATED_NON_INVENTORY_COGS_NON_COHORT]: {
        statementType: StatementType.INCOME_STATEMENT,
        name: "Non-Cohort-able Non-Inventory COGS",
        category: IncomeStatementCategory.EXPENSES,
        code: AccountLabels.EXPENSES_REVENUE_RELATED_NON_INVENTORY_COGS_NON_COHORT,
    },

    [AccountLabels.INVESTMENTS_SALES_AND_MARKETING]: {
        statementType: StatementType.INCOME_STATEMENT,
        name: "Sales & Marketing Expense",
        category: IncomeStatementCategory.INVESTMENTS,
        code: AccountLabels.INVESTMENTS_SALES_AND_MARKETING,
    },
    [AccountLabels.INVESTMENTS_EQUITY]: {
        statementType: StatementType.INCOME_STATEMENT,
        name: "Equity Investments",
        category: IncomeStatementCategory.INVESTMENTS,
        code: AccountLabels.INVESTMENTS_EQUITY,
    },
    [AccountLabels.INVESTMENTS_INVENTORY]: {
        statementType: StatementType.INCOME_STATEMENT,
        name: "Inventory Purchases",
        category: IncomeStatementCategory.INVESTMENTS,
        code: AccountLabels.INVESTMENTS_INVENTORY,
    },
    [AccountLabels.INVESTMENTS_LOAN]: {
        statementType: StatementType.INCOME_STATEMENT,
        name: "Loan Originations",
        category: IncomeStatementCategory.INVESTMENTS,
        code: AccountLabels.INVESTMENTS_LOAN,
    },
    [AccountLabels.INVESTMENTS_OTHER]: {
        statementType: StatementType.INCOME_STATEMENT,
        name: "Other Investments",
        category: IncomeStatementCategory.INVESTMENTS,
        code: AccountLabels.INVESTMENTS_OTHER,
    },

    [AccountLabels.BOOKINGS_COHORT]: {
        category: IncomeStatementCategory.BOOKINGS,
        statementType: StatementType.INCOME_STATEMENT,
        name: "Cohort-able Bookings",
        code: AccountLabels.BOOKINGS_COHORT,
    },
    [AccountLabels.BOOKINGS_NON_COHORT]: {
        category: IncomeStatementCategory.BOOKINGS,
        statementType: StatementType.INCOME_STATEMENT,
        name: "Non Cohort-able Bookings",
        code: AccountLabels.BOOKINGS_NON_COHORT,
    },

    [AccountLabels.FINANCING_DEBT_RAISED]: {
        category: IncomeStatementCategory.FINANCING,
        statementType: StatementType.INCOME_STATEMENT,
        name: "Debt Raised",
        code: AccountLabels.FINANCING_DEBT_RAISED,
    },
    [AccountLabels.FINANCING_DEBT_SERVICES_PRINCIPAL]: {
        category: IncomeStatementCategory.FINANCING,
        statementType: StatementType.INCOME_STATEMENT,
        name: "Repayment of Principal",
        code: AccountLabels.FINANCING_DEBT_SERVICES_PRINCIPAL,
    },
    [AccountLabels.FINANCING_DEBT_SERVICES_INTEREST]: {
        category: IncomeStatementCategory.FINANCING,
        statementType: StatementType.INCOME_STATEMENT,
        name: "Interest Expense",
        code: AccountLabels.FINANCING_DEBT_SERVICES_INTEREST,
    },
    [AccountLabels.FINANCING_DEBT_SERVICES_FEES]: {
        category: IncomeStatementCategory.FINANCING,
        statementType: StatementType.INCOME_STATEMENT,
        name: "Fees related to Debt Services",
        code: AccountLabels.FINANCING_DEBT_SERVICES_FEES,
    },
    [AccountLabels.FINANCING_EQUITY_RAISED]: {
        category: IncomeStatementCategory.FINANCING,
        statementType: StatementType.INCOME_STATEMENT,
        name: "Equity Raised",
        code: AccountLabels.FINANCING_EQUITY_RAISED,
    },
    [AccountLabels.FINANCING_EQUITY_SERVICES_PRINCIPAL]: {
        category: IncomeStatementCategory.FINANCING,
        statementType: StatementType.INCOME_STATEMENT,
        name: "Return of Equity Capital",
        code: AccountLabels.FINANCING_EQUITY_SERVICES_PRINCIPAL,
    },
    [AccountLabels.FINANCING_EQUITY_SERVICES_INCOME]: {
        category: IncomeStatementCategory.FINANCING,
        statementType: StatementType.INCOME_STATEMENT,
        name: "Dividends",
        code: AccountLabels.FINANCING_EQUITY_SERVICES_INCOME,
    },

    [AccountLabels.GROSS_CASHFLOWS_COHORT]: {
        category: IncomeStatementCategory.GROSS_CASHFLOWS,
        statementType: StatementType.INCOME_STATEMENT,
        name: "Cohort-able Gross Merchandise Value",
        code: AccountLabels.GROSS_CASHFLOWS_COHORT,
    },
    [AccountLabels.GROSS_CASHFLOWS_NON_COHORT]: {
        category: IncomeStatementCategory.GROSS_CASHFLOWS,
        statementType: StatementType.INCOME_STATEMENT,
        name: "Non Cohort-able Gross Merchandise Value",
        code: AccountLabels.GROSS_CASHFLOWS_NON_COHORT,
    },
    [AccountLabels.GROSS_CASHFLOWS_NON_RECURRING]: {
        category: IncomeStatementCategory.GROSS_CASHFLOWS,
        statementType: StatementType.INCOME_STATEMENT,
        name: "Non-Recurring Gross Merchandise Value",
        code: AccountLabels.GROSS_CASHFLOWS_NON_RECURRING,
    },

    [AccountLabels.OTHER_INCOME_EXPENSE_NET_OTHER_INCOME]: {
        category: IncomeStatementCategory.OTHER_INCOME_EXPENSE_NET,
        statementType: StatementType.INCOME_STATEMENT,
        name: "Other Income (Positive)",
        code: AccountLabels.OTHER_INCOME_EXPENSE_NET_OTHER_INCOME,
    },
    [AccountLabels.OTHER_INCOME_EXPENSE_NET_OTHER_INCOME_NEGATIVE]: {
        category: IncomeStatementCategory.OTHER_INCOME_EXPENSE_NET,
        statementType: StatementType.INCOME_STATEMENT,
        name: "Other Income (Negative)",
        code: AccountLabels.OTHER_INCOME_EXPENSE_NET_OTHER_INCOME_NEGATIVE,
    },
    [AccountLabels.OTHER_INCOME_EXPENSE_NET_OTHER_EXPENSE]: {
        category: IncomeStatementCategory.OTHER_INCOME_EXPENSE_NET,
        statementType: StatementType.INCOME_STATEMENT,
        name: "Other Expense (Positive)",
        code: AccountLabels.OTHER_INCOME_EXPENSE_NET_OTHER_EXPENSE,
    },
    [AccountLabels.OTHER_INCOME_EXPENSE_NET_OTHER_EXPENSE_NEGATIVE]: {
        category: IncomeStatementCategory.OTHER_INCOME_EXPENSE_NET,
        statementType: StatementType.INCOME_STATEMENT,
        name: "Other Expense (Negative)",
        code: AccountLabels.OTHER_INCOME_EXPENSE_NET_OTHER_EXPENSE_NEGATIVE,
    },

    [AccountLabels.REPORTED_NET_INCOME]: {
        category: OtherCategory.REPORTED,
        statementType: StatementType.INCOME_STATEMENT,
        name: "Net Income (As Reported)",
        code: AccountLabels.REPORTED_NET_INCOME,
    },

    [AccountLabels.CURRENT_ASSETS_CASH_AND_CASH_EQUIVALENTS]: {
        category: BalanceSheetCategory.ASSETS,
        statementType: StatementType.BALANCE_SHEET,
        name: "Cash and Cash Equivalents",
        code: AccountLabels.CURRENT_ASSETS_CASH_AND_CASH_EQUIVALENTS,
    },
    [AccountLabels.CURRENT_ASSETS_DEFERRED_EXPENSES]: {
        category: BalanceSheetCategory.ASSETS,
        statementType: StatementType.BALANCE_SHEET,
        name: "Deferred Expenses (Current Assets)",
        code: AccountLabels.CURRENT_ASSETS_DEFERRED_EXPENSES,
    },
    [AccountLabels.CURRENT_ASSETS_DERIVATIVES_AND_HEDGES]: {
        category: BalanceSheetCategory.ASSETS,
        statementType: StatementType.BALANCE_SHEET,
        name: "Derivatives and Hedges (Current Assets)",
        code: AccountLabels.CURRENT_ASSETS_DERIVATIVES_AND_HEDGES,
    },
    [AccountLabels.CURRENT_ASSETS_NET_CONTRACTS_WITH_CUSTOMERS]: {
        category: BalanceSheetCategory.ASSETS,
        statementType: StatementType.BALANCE_SHEET,
        name: "Net Contracts with Customers (Current Assets)",
        code: AccountLabels.CURRENT_ASSETS_NET_CONTRACTS_WITH_CUSTOMERS,
    },
    [AccountLabels.CURRENT_ASSETS_NET_INVENTORY]: {
        category: BalanceSheetCategory.ASSETS,
        statementType: StatementType.BALANCE_SHEET,
        name: "Net Inventory (Current Assets)",
        code: AccountLabels.CURRENT_ASSETS_NET_INVENTORY,
    },
    [AccountLabels.CURRENT_ASSETS_NET_RECEIVABLES]: {
        category: BalanceSheetCategory.ASSETS,
        statementType: StatementType.BALANCE_SHEET,
        name: "Accounts Receivables (Current Assets)",
        code: AccountLabels.CURRENT_ASSETS_NET_RECEIVABLES,
    },
    [AccountLabels.CURRENT_ASSETS_OTHER_CURRENT_ASSETS]: {
        category: BalanceSheetCategory.ASSETS,
        statementType: StatementType.BALANCE_SHEET,
        name: "Other Current Assets (Current Assets)",
        code: AccountLabels.CURRENT_ASSETS_OTHER_CURRENT_ASSETS,
    },
    [AccountLabels.CURRENT_ASSETS_PREPAID_EXPENSES]: {
        category: BalanceSheetCategory.ASSETS,
        statementType: StatementType.BALANCE_SHEET,
        name: "Prepaid Expenses (Current Assets)",
        code: AccountLabels.CURRENT_ASSETS_PREPAID_EXPENSES,
    },

    [AccountLabels.NONCURRENT_ASSETS_DEFERRED_EXPENSES]: {
        category: BalanceSheetCategory.ASSETS,
        statementType: StatementType.BALANCE_SHEET,
        name: "Deferred Expenses (Non-Current)",
        code: AccountLabels.NONCURRENT_ASSETS_DEFERRED_EXPENSES,
    },
    [AccountLabels.NONCURRENT_ASSETS_DERIVATIVES_AND_HEDGES]: {
        category: BalanceSheetCategory.ASSETS,
        statementType: StatementType.BALANCE_SHEET,
        name: "Derivatives and Hedges (Non-Current)",
        code: AccountLabels.NONCURRENT_ASSETS_DERIVATIVES_AND_HEDGES,
    },
    [AccountLabels.NONCURRENT_ASSETS_INVENTORY]: {
        category: BalanceSheetCategory.ASSETS,
        statementType: StatementType.BALANCE_SHEET,
        name: "Inventory (Non-Current)",
        code: AccountLabels.NONCURRENT_ASSETS_INVENTORY,
    },
    [AccountLabels.NONCURRENT_ASSETS_LONGTERM_INVESTMENTS_AND_RECEIVABLES]: {
        category: BalanceSheetCategory.ASSETS,
        statementType: StatementType.BALANCE_SHEET,
        name: "Long-Term Investments and Receivables",
        code: AccountLabels.NONCURRENT_ASSETS_LONGTERM_INVESTMENTS_AND_RECEIVABLES,
    },
    [AccountLabels.NONCURRENT_ASSETS_NET_CONTRACTS_WITH_CUSTOMERS]: {
        category: BalanceSheetCategory.ASSETS,
        statementType: StatementType.BALANCE_SHEET,
        name: "Net Contracts with Customers (Non-Current)",
        code: AccountLabels.NONCURRENT_ASSETS_NET_CONTRACTS_WITH_CUSTOMERS,
    },
    [AccountLabels.NONCURRENT_ASSETS_NET_INTANGIBLE_ASSETS_AND_GOODWILL]: {
        category: BalanceSheetCategory.ASSETS,
        statementType: StatementType.BALANCE_SHEET,
        name: "Net Intangible Assets and Goodwill",
        code: AccountLabels.NONCURRENT_ASSETS_NET_INTANGIBLE_ASSETS_AND_GOODWILL,
    },
    [AccountLabels.NONCURRENT_ASSETS_OTHER_ASSETS]: {
        category: BalanceSheetCategory.ASSETS,
        statementType: StatementType.BALANCE_SHEET,
        name: "Other Non-Current Assets",
        code: AccountLabels.NONCURRENT_ASSETS_OTHER_ASSETS,
    },
    [AccountLabels.NONCURRENT_ASSETS_PREPAID_EXPENSES]: {
        category: BalanceSheetCategory.ASSETS,
        statementType: StatementType.BALANCE_SHEET,
        name: "Prepaid Expenses (Non-Current)",
        code: AccountLabels.NONCURRENT_ASSETS_PREPAID_EXPENSES,
    },
    [AccountLabels.NONCURRENT_ASSETS_PROPERTY_PLANT_AND_EQUIPMENT]: {
        category: BalanceSheetCategory.ASSETS,
        statementType: StatementType.BALANCE_SHEET,
        name: "Property, Plant and Equipment (PP&E)",
        code: AccountLabels.NONCURRENT_ASSETS_PROPERTY_PLANT_AND_EQUIPMENT,
    },
    [AccountLabels.NONCURRENT_ASSETS_RESTRICTED_CASH_AND_CASH_EQUIVALENTS]: {
        category: BalanceSheetCategory.ASSETS,
        statementType: StatementType.BALANCE_SHEET,
        name: "Restricted Cash and Cash Equivalents",
        code: AccountLabels.NONCURRENT_ASSETS_RESTRICTED_CASH_AND_CASH_EQUIVALENTS,
    },

    [AccountLabels.CURRENT_LIABILITIES_ACCOUNTS_PAYABLE]: {
        category: BalanceSheetCategory.LIABILITIES,
        statementType: StatementType.BALANCE_SHEET,
        name: "Accounts Payable",
        code: AccountLabels.CURRENT_LIABILITIES_ACCOUNTS_PAYABLE,
    },
    [AccountLabels.CURRENT_LIABILITIES_DEBT]: {
        category: BalanceSheetCategory.LIABILITIES,
        statementType: StatementType.BALANCE_SHEET,
        name: "Current Debt",
        code: AccountLabels.CURRENT_LIABILITIES_DEBT,
    },
    [AccountLabels.CURRENT_LIABILITIES_DEFERRED_REVENUE]: {
        category: BalanceSheetCategory.LIABILITIES,
        statementType: StatementType.BALANCE_SHEET,
        name: "Deferred Revenue",
        code: AccountLabels.CURRENT_LIABILITIES_DEFERRED_REVENUE,
    },
    [AccountLabels.CURRENT_LIABILITIES_DERIVATIVES_AND_HEDGES]: {
        category: BalanceSheetCategory.LIABILITIES,
        statementType: StatementType.BALANCE_SHEET,
        name: "Derivatives and Hedges (Current)",
        code: AccountLabels.CURRENT_LIABILITIES_DERIVATIVES_AND_HEDGES,
    },
    [AccountLabels.CURRENT_LIABILITIES_OTHER_LIABILITIES]: {
        category: BalanceSheetCategory.LIABILITIES,
        statementType: StatementType.BALANCE_SHEET,
        name: "Other Current Liabilities",
        code: AccountLabels.CURRENT_LIABILITIES_OTHER_LIABILITIES,
    },
    [AccountLabels.CURRENT_LIABILITIES_PROVISION_AND_ACCRUALS]: {
        category: BalanceSheetCategory.LIABILITIES,
        statementType: StatementType.BALANCE_SHEET,
        name: "Provision and Accruals (Current)",
        code: AccountLabels.CURRENT_LIABILITIES_PROVISION_AND_ACCRUALS,
    },
    [AccountLabels.CURRENT_LIABILITIES_PROVISION_FOR_LOSSES_ON_CONTRACTS]: {
        category: BalanceSheetCategory.LIABILITIES,
        statementType: StatementType.BALANCE_SHEET,
        name: "Provision for Losses on Contracts (Current)",
        code: AccountLabels.CURRENT_LIABILITIES_PROVISION_FOR_LOSSES_ON_CONTRACTS,
    },
    [AccountLabels.CURRENT_LIABILITIES_PROVISION_FOR_REFUNDS]: {
        category: BalanceSheetCategory.LIABILITIES,
        statementType: StatementType.BALANCE_SHEET,
        name: "Provision for Refunds (Current)",
        code: AccountLabels.CURRENT_LIABILITIES_PROVISION_FOR_REFUNDS,
    },

    [AccountLabels.NONCURRENT_LIABILITIES_ACCOUNTS_PAYABLE]: {
        category: BalanceSheetCategory.LIABILITIES,
        statementType: StatementType.BALANCE_SHEET,
        name: "Accounts Payable (Non-Current)",
        code: AccountLabels.NONCURRENT_LIABILITIES_ACCOUNTS_PAYABLE,
    },
    [AccountLabels.NONCURRENT_LIABILITIES_DEFERRED_REVENUE]: {
        category: BalanceSheetCategory.LIABILITIES,
        statementType: StatementType.BALANCE_SHEET,
        name: "Deferred Revenue (Non-Current)",
        code: AccountLabels.NONCURRENT_LIABILITIES_DEFERRED_REVENUE,
    },
    [AccountLabels.NONCURRENT_LIABILITIES_DERIVATIVES_HEDGES]: {
        category: BalanceSheetCategory.LIABILITIES,
        statementType: StatementType.BALANCE_SHEET,
        name: "Derivatives and Hedges (Non-Current)",
        code: AccountLabels.NONCURRENT_LIABILITIES_DERIVATIVES_HEDGES,
    },
    [AccountLabels.NONCURRENT_LIABILITIES_LONGTERM_DEBT]: {
        category: BalanceSheetCategory.LIABILITIES,
        statementType: StatementType.BALANCE_SHEET,
        name: "Long-Term Debt",
        code: AccountLabels.NONCURRENT_LIABILITIES_LONGTERM_DEBT,
    },
    [AccountLabels.NONCURRENT_LIABILITIES_OTHER_LIABILITIES]: {
        category: BalanceSheetCategory.LIABILITIES,
        statementType: StatementType.BALANCE_SHEET,
        name: "Other Non-Current Liabilities",
        code: AccountLabels.NONCURRENT_LIABILITIES_OTHER_LIABILITIES,
    },
    [AccountLabels.NONCURRENT_LIABILITIES_PROVISION_AND_ACCRUALS]: {
        category: BalanceSheetCategory.LIABILITIES,
        statementType: StatementType.BALANCE_SHEET,
        name: "Provision and Accruals (Non-Current)",
        code: AccountLabels.NONCURRENT_LIABILITIES_PROVISION_AND_ACCRUALS,
    },
    [AccountLabels.NONCURRENT_LIABILITIES_PROVISION_FOR_LOSSES_ON_CONTRACTS]: {
        category: BalanceSheetCategory.LIABILITIES,
        statementType: StatementType.BALANCE_SHEET,
        name: "Provision for Losses on Contracts (Non-Current)",
        code: AccountLabels.NONCURRENT_LIABILITIES_PROVISION_FOR_LOSSES_ON_CONTRACTS,
    },
    [AccountLabels.NONCURRENT_LIABILITIES_PROVISION_FOR_REFUNDS]: {
        category: BalanceSheetCategory.LIABILITIES,
        statementType: StatementType.BALANCE_SHEET,
        name: "Provision for Refunds (Non-Current)",
        code: AccountLabels.NONCURRENT_LIABILITIES_PROVISION_FOR_REFUNDS,
    },

    [AccountLabels.EQUITY_PAID_IN_CAPITAL]: {
        category: BalanceSheetCategory.EQUITY,
        statementType: StatementType.BALANCE_SHEET,
        name: "Paid-In Capital",
        code: AccountLabels.EQUITY_PAID_IN_CAPITAL,
    },
    [AccountLabels.EQUITY_RETAINED_EARNINGS]: {
        category: BalanceSheetCategory.EQUITY,
        statementType: StatementType.BALANCE_SHEET,
        name: "Retained Earnings",
        code: AccountLabels.EQUITY_RETAINED_EARNINGS,
    },

    [AccountLabels.EXCLUDED_BLANK]: {
        category: OtherCategory.EXCLUDED,
        statementType: StatementType.MIXED,
        name: "Excluded Blank",
        code: AccountLabels.EXCLUDED_BLANK,
    },
    [AccountLabels.EXCLUDED_CONTRA_ACCOUNT]: {
        category: OtherCategory.EXCLUDED,
        statementType: StatementType.MIXED,
        name: "Excluded Contra Account",
        code: AccountLabels.EXCLUDED_CONTRA_ACCOUNT,
    },
    [AccountLabels.EXCLUDED_DIFFERENCE]: {
        category: OtherCategory.EXCLUDED,
        statementType: StatementType.MIXED,
        name: "Excluded Difference",
        code: AccountLabels.EXCLUDED_DIFFERENCE,
    },
    [AccountLabels.EXCLUDED_SUM]: {
        category: OtherCategory.EXCLUDED,
        statementType: StatementType.MIXED,
        name: "Excluded Sum",
        code: AccountLabels.EXCLUDED_SUM,
    },
    [AccountLabels.REPORTED_CURRENT_ASSETS]: {
        category: OtherCategory.REPORTED,
        statementType: StatementType.BALANCE_SHEET,
        name: "Reported Current Assets",
        code: AccountLabels.REPORTED_CURRENT_ASSETS,
    },
    [AccountLabels.REPORTED_NONCURRENT_ASSETS]: {
        category: OtherCategory.REPORTED,
        statementType: StatementType.BALANCE_SHEET,
        name: "Reported Non-Current Assets",
        code: AccountLabels.REPORTED_NONCURRENT_ASSETS,
    },
    [AccountLabels.REPORTED_CURRENT_LIABILITIES]: {
        category: OtherCategory.REPORTED,
        statementType: StatementType.BALANCE_SHEET,
        name: "Reported Current Liabilities",
        code: AccountLabels.REPORTED_CURRENT_LIABILITIES,
    },
    [AccountLabels.REPORTED_NONCURRENT_LIABILITIES]: {
        category: OtherCategory.REPORTED,
        statementType: StatementType.BALANCE_SHEET,
        name: "Reported Non-Current Liabilities",
        code: AccountLabels.REPORTED_NONCURRENT_LIABILITIES,
    },
    [AccountLabels.REPORTED_TOTAL_EQUITY]: {
        category: OtherCategory.REPORTED,
        statementType: StatementType.BALANCE_SHEET,
        name: "Reported Total Equity",
        code: AccountLabels.REPORTED_TOTAL_EQUITY,
    },
    [AccountLabels.REPORTED_COST_OF_GOODS_SOLD]: {
        category: OtherCategory.REPORTED,
        statementType: StatementType.INCOME_STATEMENT,
        name: "Reported Cost of Goods Sold",
        code: AccountLabels.REPORTED_COST_OF_GOODS_SOLD,
    },
    [AccountLabels.REPORTED_GROSS_MARGIN]: {
        category: OtherCategory.REPORTED,
        statementType: StatementType.INCOME_STATEMENT,
        name: "Reported Gross Margin",
        code: AccountLabels.REPORTED_GROSS_MARGIN,
    },
    [AccountLabels.REPORTED_TOTAL_OPERATING_EXPENSES]: {
        category: OtherCategory.REPORTED,
        statementType: StatementType.INCOME_STATEMENT,
        name: "Reported Total Operating Expenses",
        code: AccountLabels.REPORTED_TOTAL_OPERATING_EXPENSES,
    },
    [AccountLabels.REPORTED_EBITDA]: {
        category: OtherCategory.REPORTED,
        statementType: StatementType.INCOME_STATEMENT,
        name: "Reported EBITDA",
        code: AccountLabels.REPORTED_EBITDA,
    },
    [AccountLabels.EXCLUDED_UNKNOWN]: {
        category: OtherCategory.EXCLUDED,
        statementType: StatementType.BALANCE_SHEET,
        name: "Excluded Unknown",
        code: AccountLabels.EXCLUDED_UNKNOWN,
    },
} as const;

// Colors
export const EXCLUDED_COLOR = "#FF6B6B";
export const REPORTED_COLOR = "#FFFFFF";

// Income Statement
export const REVENUES_COLOR = "#08BDBA";
export const EXPENSES_COLOR = "#DFBF03";
export const INVESTMENTS_COLOR = "#4A66F4";
export const BOOKINGS_COLOR = "#AEC3FC";
export const GROSS_CASHFLOWS_COLOR = "#FBBF24";
export const OTHER_INCOME_EXPENSE_NET_COLOR = "#10B981";
export const FINANCING_COLOR = "#DA348F";

// Balance Sheet
export const EQUITY_COLOR = "#28A138";
export const ASSETS_COLOR = "#F9a43f";
export const LIABILITIES_COLOR = "#B215DA";

export const CATEGORY_METADATA: Record<
    AccountCategory,
    { color: string; name: string }
> = {
    [IncomeStatementCategory.REVENUES]: {
        color: REVENUES_COLOR,
        name: "Revenues",
    },
    [IncomeStatementCategory.EXPENSES]: {
        color: EXPENSES_COLOR,
        name: "Expenses",
    },
    [IncomeStatementCategory.INVESTMENTS]: {
        color: INVESTMENTS_COLOR,
        name: "Investments",
    },
    [IncomeStatementCategory.BOOKINGS]: {
        color: BOOKINGS_COLOR,
        name: "Bookings",
    },
    [IncomeStatementCategory.GROSS_CASHFLOWS]: {
        color: GROSS_CASHFLOWS_COLOR,
        name: "Gross Cashflows",
    },
    [IncomeStatementCategory.OTHER_INCOME_EXPENSE_NET]: {
        color: OTHER_INCOME_EXPENSE_NET_COLOR,
        name: "Other Income & Expense",
    },
    [IncomeStatementCategory.FINANCING]: {
        color: FINANCING_COLOR,
        name: "Financing",
    },
    [BalanceSheetCategory.EQUITY]: {
        color: EQUITY_COLOR,
        name: "Equity",
    },
    [BalanceSheetCategory.ASSETS]: {
        color: ASSETS_COLOR,
        name: "Assets",
    },
    [BalanceSheetCategory.LIABILITIES]: {
        color: LIABILITIES_COLOR,
        name: "Liabilities",
    },
    [OtherCategory.REPORTED]: {
        color: REPORTED_COLOR,
        name: "Reported",
    },
    [OtherCategory.EXCLUDED]: {
        color: EXCLUDED_COLOR,
        name: "Excluded",
    },
};
